import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCompany } from '../../hooks/useCompany';
import { tellerAccountAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Users,
  Plus,
  DollarSign,
  Wallet,
  CheckCircle2,
  XCircle,
  Edit,
  Monitor,
  User,
  BookOpen,
  AlertCircle,
} from 'lucide-react';

const formatCurrency = (value, currency = 'USD') => {
  if (currency === 'LRD') {
    return `L$${new Intl.NumberFormat('en-US').format(value || 0)}`;
  }
  return `$${new Intl.NumberFormat('en-US').format(value || 0)}`;
};

function CreateTellerModal({ isOpen, onClose, onSuccess, companyId }) {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [name, setName] = useState('');

  // Fetch users with "teller" role
  const { data: tellerUsers = [], isLoading: loadingUsers } = useQuery({
    queryKey: ['teller-users', companyId],
    queryFn: () => tellerAccountAPI.getTellerUsers(companyId).then(res => res.data),
    enabled: isOpen && !!companyId,
  });

  // Filter to only unassigned users
  const availableUsers = tellerUsers.filter(u => !u.is_assigned);

  const mutation = useMutation({
    mutationFn: (data) => tellerAccountAPI.create(data),
    onSuccess: () => {
      toast.success('Teller account created successfully');
      onSuccess();
      onClose();
      setSelectedUserId('');
      setName('');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Failed to create teller account');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUserId || !name) {
      toast.error('Please select a user and enter a name');
      return;
    }
    mutation.mutate({
      company_id: companyId,
      name,
      assigned_user_id: selectedUserId,
    });
  };

  // Auto-fill name when user is selected
  const handleUserChange = (userId) => {
    setSelectedUserId(userId);
    const user = tellerUsers.find(u => u.id === userId);
    if (user && !name) {
      setName(`${user.first_name} ${user.last_name}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">Create Teller Account</h3>
        
        {/* Info Banner */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Auto-Generated Features:</p>
            <ul className="list-disc ml-4 mt-1 text-xs">
              <li>Teller Code: <span className="font-mono">DEA-FINS-TLR-XXX</span></li>
              <li>Linked COA Asset Account (1500 series)</li>
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* User Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Assign Teller User <span className="text-red-500">*</span>
            </label>
            {loadingUsers ? (
              <div className="w-full px-3 py-2 bg-muted border border-border rounded-md text-muted-foreground">
                Loading users...
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-700">
                  No available teller users. Create a user with "Teller" role in User Management first.
                </p>
              </div>
            ) : (
              <select
                value={selectedUserId}
                onChange={(e) => handleUserChange(e.target.value)}
                className="w-full px-3 py-2 bg-background border border-border rounded-md"
                data-testid="select-teller-user"
              >
                <option value="">Select a user...</option>
                {availableUsers.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-muted-foreground mt-1">
              Only users with "Teller" role are shown
            </p>
          </div>

          {/* Teller Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Teller Station Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., John Doe - Window 1"
              className="w-full px-3 py-2 bg-background border border-border rounded-md"
              data-testid="input-teller-name"
            />
          </div>

          {/* Preview */}
          {selectedUserId && name && (
            <div className="p-3 bg-muted/50 rounded-lg space-y-1 text-sm">
              <p><span className="text-muted-foreground">Code:</span> <span className="font-mono font-medium">DEA-FINS-TLR-XXX</span> <span className="text-xs text-muted-foreground">(auto-generated)</span></p>
              <p><span className="text-muted-foreground">COA Account:</span> <span className="font-mono font-medium">15XX - Teller Cash</span></p>
              <p><span className="text-muted-foreground">Starting Balance:</span> $0.00 / L$0.00 <span className="text-xs text-muted-foreground">(from vault allocation)</span></p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-md hover:bg-muted"
              data-testid="button-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !selectedUserId || !name}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50"
              data-testid="button-create-teller"
            >
              {mutation.isPending ? 'Creating...' : 'Create Teller Account'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TellerAccounts() {
  const { selectedCompany, getDFSCompany, companies } = useCompany();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const dfsCompany = getDFSCompany ? getDFSCompany() : selectedCompany;
  const companyId = dfsCompany?.id;

  // Debug: Log companies and DFS company
  console.log('Companies loaded:', companies?.length, 'DFS Company:', dfsCompany?.name, 'ID:', companyId);

  const { data: tellers = [], isLoading } = useQuery({
    queryKey: ['teller-accounts', companyId],
    queryFn: () => tellerAccountAPI.getAll(companyId).then((res) => res.data),
    enabled: !!companyId,
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries(['teller-accounts', companyId]);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const activeTellers = tellers.filter((t) => t.is_active);
  const tellersWithSession = tellers.filter((t) => t.has_active_session);
  const totalUSD = tellers.reduce((sum, t) => sum + (t.usd_current_balance || 0), 0);
  const totalLRD = tellers.reduce((sum, t) => sum + (t.lrd_current_balance || 0), 0);

  return (
    <div className="space-y-6" data-testid="teller-accounts-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Teller Accounts</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage teller workstations and cash positions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          data-testid="button-create-teller"
        >
          <Plus className="h-4 w-4" />
          Add Teller
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Tellers</span>
          </div>
          <p className="text-2xl font-semibold">{tellers.length}</p>
          <p className="text-xs text-muted-foreground">{activeTellers.length} active</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Monitor className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Active Sessions</span>
          </div>
          <p className="text-2xl font-semibold">{tellersWithSession.length}</p>
          <p className="text-xs text-muted-foreground">tellers with open sessions</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="text-sm text-muted-foreground">Total USD</span>
          </div>
          <p className="text-2xl font-semibold text-green-600">{formatCurrency(totalUSD, 'USD')}</p>
          <p className="text-xs text-muted-foreground">across all tellers</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Wallet className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-muted-foreground">Total LRD</span>
          </div>
          <p className="text-2xl font-semibold text-blue-600">{formatCurrency(totalLRD, 'LRD')}</p>
          <p className="text-xs text-muted-foreground">across all tellers</p>
        </div>
      </div>

      {/* Teller Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {tellers.map((teller) => (
          <div
            key={teller.id}
            className={`bg-card border rounded-lg p-4 ${
              teller.has_active_session ? 'border-green-500/50' : 'border-border'
            }`}
            data-testid={`teller-card-${teller.code}`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                  teller.has_active_session ? 'bg-green-500/10' : 'bg-muted'
                }`}>
                  <Monitor className={`h-4 w-4 ${
                    teller.has_active_session ? 'text-green-500' : 'text-muted-foreground'
                  }`} />
                </div>
                <div>
                  <p className="font-semibold text-sm">{teller.code}</p>
                  <p className="text-xs text-muted-foreground">{teller.name}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {teller.has_active_session ? (
                  <span className="flex items-center gap-1 text-xs text-green-600 bg-green-500/10 px-2 py-1 rounded-full">
                    <CheckCircle2 className="h-3 w-3" />
                    Active
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">
                    <XCircle className="h-3 w-3" />
                    Idle
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">USD Balance:</span>
                <span className="font-mono font-medium text-green-600">
                  {formatCurrency(teller.usd_current_balance, 'USD')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">LRD Balance:</span>
                <span className="font-mono font-medium text-blue-600">
                  {formatCurrency(teller.lrd_current_balance, 'LRD')}
                </span>
              </div>
            </div>

            {teller.assigned_user_id && (
              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <User className="h-3 w-3" />
                  <span>{teller.assigned_user_name || 'Unassigned'}</span>
                </div>
                {teller.coa_account_code && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                    <BookOpen className="h-3 w-3" />
                    <span>COA: {teller.coa_account_code}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {tellers.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            No teller accounts found. Click "Add Teller" to create one.
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateTellerModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleRefresh}
        companyId={companyId}
      />
    </div>
  );
}
